'use client'

/**
 * The whole of `/app/settings/api-keys`: the load, the loading branch and the layout.
 *
 * The route file keeps only the default export Next.js resolves by name and delegates to this —
 * "every file Next.js resolves by name is an adapter and owns nothing" (ADR 0010).
 */

import { useEffect, useState } from 'react'
import { ApiKeyList } from './api-key-list'
import { CreateApiKeyDialog } from './create-api-key-dialog'
import { listApiKeys } from '../actions/api-keys'

interface ApiKeyDisplay {
  id: string
  name: string
  keyPrefix: string
  createdAt: string
  lastUsedAt: string | null
}

export function ApiKeysView() {
  const [keys, setKeys] = useState<ApiKeyDisplay[]>([])
  const [loading, setLoading] = useState(true)

  async function loadKeys() {
    try {
      const result = await listApiKeys()
      setKeys(result)
    } catch (error) {
      console.error('Failed to load keys:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadKeys()
  }, [])

  return (
    <div className="container max-w-2xl py-8">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">API Keys</h1>
          <p className="text-muted-foreground mt-1">
            Manage API keys for external integrations like Apple Shortcuts.
          </p>
        </div>

        <div className="bg-muted/50 space-y-2 rounded-lg p-4 text-sm">
          <p className="font-medium">How to use API keys:</p>
          <ol className="text-muted-foreground list-inside list-decimal space-y-1">
            <li>Create an API key below</li>
            <li>Copy the key (it&apos;s only shown once)</li>
            <li>
              In Apple Shortcuts, use &ldquo;Get Contents of URL&rdquo; action
            </li>
            <li>
              Set URL to:{' '}
              <code className="bg-muted rounded px-1">
                https://your-domain/api/capture
              </code>
            </li>
            <li>Add header: Authorization = Bearer YOUR_KEY</li>
            <li>Set request body with your content</li>
          </ol>
        </div>

        <div className="flex justify-end">
          <CreateApiKeyDialog onKeyCreated={loadKeys} />
        </div>

        {loading ? (
          <div className="text-muted-foreground py-8 text-center">
            Loading...
          </div>
        ) : (
          <ApiKeyList keys={keys} onKeyRevoked={loadKeys} />
        )}
      </div>
    </div>
  )
}
